import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { toWorker } from "../util/toWorker.ts";
import { useDesktopStore } from "../store.ts";

// ── Types ─────────────────────────────────────────────────────────────────────

type EditorPlayer = {
	pid: number;
	firstName: string;
	lastName: string;
	pos: string;
	age: number;
	tid: number;
	teamAbbrev: string;
	college: string;
	country: string;
	ovr: number;
	pot: number;
	contract?: { amount: number; exp: number };
	draft?: { year: number; round: number; pick: number };
	ratings: Record<string, number>;
	year?: string;
	status?: string;
	rank?: number;
	hometown?: string;
	// college-only stats
	stats?: { gp: number; pts: number; reb: number; ast: number };
	isCollegePlayer?: boolean;
	isHsPlayer?: boolean;
};

type TeamRoster = {
	tid: number;
	team: { tid: number; abbrev: string; region: string; name: string };
	players: EditorPlayer[];
};

type CollegeTeamEntry = { tid: number; school: string; players: EditorPlayer[] };

type EditorData = {
	teamRosters: TeamRoster[];
	freeAgents: EditorPlayer[];
	draftClasses: EditorPlayer[];
	retired: EditorPlayer[];
	collegeTeams: CollegeTeamEntry[];
	hsTop100: EditorPlayer[];
	hsJuniors: EditorPlayer[];
	season: number;
};

type SortDir = "asc" | "desc";
type SortKey = "name" | "pos" | "age" | "ovr" | "pot" | "school" | "rank" | "pts" | "reb" | "ast" | string;

// ── Constants ─────────────────────────────────────────────────────────────────

const RATING_KEYS = [
	"hgt","stre","spd","jmp","endu","ins","dnk","ft","fg","tp","oiq","diq","drb","pss","reb",
] as const;

const RATING_LABELS: Record<string, string> = {
	hgt:"Hgt", stre:"Str", spd:"Spd", jmp:"Jmp", endu:"End",
	ins:"Ins", dnk:"Dnk", ft:"FT", fg:"FG", tp:"3P",
	oiq:"OIQ", diq:"DIQ", drb:"Drb", pss:"Pss", reb:"Reb",
};

const POSITIONS = ["PG","SG","SF","PF","C","G","F","GF","FC"];

// Comprehensive US city list — "City, ST" — for hometown dropdown
const US_CITIES = [
	"Akron, OH","Albany, NY","Albuquerque, NM","Alexandria, VA","Allentown, PA",
	"Amarillo, TX","Anaheim, CA","Anchorage, AK","Ann Arbor, MI","Arlington, TX",
	"Asheville, NC","Atlanta, GA","Augusta, GA","Aurora, CO","Austin, TX",
	"Bakersfield, CA","Baltimore, MD","Baton Rouge, LA","Beaumont, TX","Birmingham, AL",
	"Boise, ID","Boston, MA","Bridgeport, CT","Brooklyn, NY","Buffalo, NY",
	"Burlington, VT","Camden, NJ","Cape Coral, FL","Cedar Rapids, IA","Charleston, SC",
	"Charleston, WV","Charlotte, NC","Chattanooga, TN","Chicago, IL","Cincinnati, OH",
	"Cleveland, OH","Colorado Springs, CO","Columbia, SC","Columbus, GA","Columbus, OH",
	"Compton, CA","Concord, CA","Corpus Christi, TX","Dallas, TX","Dayton, OH",
	"Denver, CO","Des Moines, IA","Detroit, MI","Durham, NC","El Paso, TX",
	"Elizabeth, NJ","Eugene, OR","Evansville, IN","Fayetteville, NC","Flint, MI",
	"Fort Collins, CO","Fort Lauderdale, FL","Fort Wayne, IN","Fort Worth, TX","Fresno, CA",
	"Gainesville, FL","Gary, IN","Grand Rapids, MI","Greensboro, NC","Hampton, VA",
	"Hartford, CT","Honolulu, HI","Houston, TX","Huntington, WV","Huntsville, AL",
	"Indianapolis, IN","Inglewood, CA","Irving, TX","Jackson, MS","Jacksonville, FL",
	"Jersey City, NJ","Kansas City, MO","Knoxville, TN","Lansing, MI","Las Vegas, NV",
	"Lexington, KY","Lincoln, NE","Little Rock, AR","Long Beach, CA","Los Angeles, CA",
	"Louisville, KY","Lubbock, TX","Madison, WI","Memphis, TN","Mesa, AZ",
	"Miami, FL","Milwaukee, WI","Minneapolis, MN","Mobile, AL","Montgomery, AL",
	"Moreno Valley, CA","Nashville, TN","New Haven, CT","New Orleans, LA","New York, NY",
	"Newark, NJ","Newport News, VA","Norfolk, VA","North Las Vegas, NV","Oakland, CA",
	"Oklahoma City, OK","Omaha, NE","Orlando, FL","Oxnard, CA","Paterson, NJ",
	"Peoria, IL","Philadelphia, PA","Phoenix, AZ","Pittsburgh, PA","Plano, TX",
	"Portland, OR","Providence, RI","Raleigh, NC","Reno, NV","Richmond, VA",
	"Riverside, CA","Rochester, NY","Sacramento, CA","Saint Paul, MN","Salt Lake City, UT",
	"San Antonio, TX","San Bernardino, CA","San Diego, CA","San Francisco, CA","San Jose, CA",
	"Santa Ana, CA","Savannah, GA","Scottsdale, AZ","Seattle, WA","Shreveport, LA",
	"Spokane, WA","Springfield, IL","Springfield, MO","Springfield, MA","St. Louis, MO",
	"Stockton, CA","Syracuse, NY","Tacoma, WA","Tampa, FL","Toledo, OH",
	"Torrance, CA","Tucson, AZ","Tulsa, OK","Virginia Beach, VA","Washington, DC",
	"Wichita, KS","Winston-Salem, NC","Worcester, MA","Yonkers, NY",
	// Mid-size / college towns
	"Abilene, TX","Altoona, PA","Anderson, IN","Annapolis, MD","Appleton, WI",
	"Athens, GA","Auburn, AL","Bangor, ME","Binghamton, NY","Bloomington, IL",
	"Bloomington, IN","Bowling Green, KY","Brockton, MA","Brookhaven, MS","Bryan, TX",
	"Canton, OH","Carbondale, IL","Champaign, IL","Chesapeake, VA","Chico, CA",
	"Clarksville, TN","Clearwater, FL","Clinton, MS","College Park, MD","Columbia, MO",
	"Compton, CA","Corona, CA","Denton, TX","Dover, DE","Duluth, MN",
	"Durham, NC","East Lansing, MI","East St. Louis, IL","Erie, PA","Eunice, LA",
	"Evanston, IL","Fairfield, CA","Fall River, MA","Fargo, ND","Florence, SC",
	"Fontana, CA","Gainesville, GA","Greenville, SC","Gulfport, MS","Hammond, LA",
	"Harrisburg, PA","Hartford, CT","Harvey, IL","Hattiesburg, MS","High Point, NC",
	"Hillside, NJ","Inglewood, CA","Irvington, NJ","Jackson, TN","Jonesboro, AR",
	"Killeen, TX","Kingsport, TN","Lake Charles, LA","Lakewood, CA","Lancaster, PA",
	"Lansing, MI","Lawrence, KS","Layton, UT","Lewisville, TX","Linden, NJ",
	"Longview, TX","Lowell, MA","Lynchburg, VA","Macon, GA","Manchester, NH",
	"McAllen, TX","Meridian, MS","Midland, TX","Monroe, LA","Muskegon, MI",
	"Myrtle Beach, SC","New Brunswick, NJ","New Iberia, LA","Norcross, GA","Norman, OK",
	"Norwalk, CA","Ocala, FL","Odessa, TX","Opelousas, LA","Orange, NJ",
	"Orlando, FL","Oshkosh, WI","Palm Bay, FL","Palmdale, CA","Pasadena, CA",
	"Pasadena, TX","Pensacola, FL","Peoria, AZ","Plainfield, NJ","Pomona, CA",
	"Pontiac, MI","Port Arthur, TX","Portsmouth, VA","Provo, UT","Pueblo, CO",
	"Reading, PA","Rialto, CA","Rockford, IL","Rocky Mount, NC","Roseville, CA",
	"Round Rock, TX","San Marcos, TX","Savannah, GA","Simi Valley, CA","Sioux Falls, SD",
	"South Bend, IN","Spartanburg, SC","Sterling Heights, MI","Tallahassee, FL",
	"Tempe, AZ","Thousand Oaks, CA","Topeka, KS","Trenton, NJ","Tyler, TX",
	"Upper Marlboro, MD","Vallejo, CA","Victorville, CA","Waco, TX","Warren, MI",
	"Waterbury, CT","West Palm Beach, FL","Westminster, CA","Wichita Falls, TX","Wilmington, DE",
	"Wilmington, NC","Youngstown, OH",
].sort();

const COUNTRIES = [
	"USA","Canada","France","Spain","Germany","Australia","Nigeria","Cameroon",
	"Latvia","Lithuania","Serbia","Croatia","Greece","Argentina","Brazil","Turkey",
	"Slovenia","Montenegro","Bosnia","Ukraine","Italy","DRC","Senegal","Mali",
	"Sweden","Dominican Republic","Puerto Rico","Jamaica","Great Britain","China",
	"Australia","New Zealand","Czech Republic","Hungary","Georgia","Denmark","Finland",
	"Israel","Lebanon","Russia","Japan","South Korea","Philippines",
].sort();

// ── Helpers ───────────────────────────────────────────────────────────────────

const ovrColor = (v: number) =>
	v >= 80 ? "var(--green)" : v >= 65 ? "inherit" : v >= 50 ? "var(--muted)" : "var(--red)";

const ratColor = (v: number) =>
	v >= 80 ? "var(--green)" : v >= 65 ? "inherit" : v >= 50 ? "var(--muted)" : "var(--red)";

// ── SearchableDropdown (autocomplete) ─────────────────────────────────────────
// Always-visible text input — type anything to filter, click a suggestion or
// press Escape/blur to close.  No need to click a button to open.

const SearchableDropdown = ({
	options,
	value,
	onSelect,
	placeholder = "Search…",
	width = 200,
}: {
	options: string[];
	value: string;
	onSelect: (v: string) => void;
	placeholder?: string;
	width?: number;
}) => {
	const [q, setQ] = useState<string | null>(null); // null = not editing
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const displayQ = q ?? value;

	const filtered = useMemo(() => {
		const lq = (q ?? "").toLowerCase().trim();
		if (!lq) return options.slice(0, 150);
		return options.filter((o) => o.toLowerCase().includes(lq)).slice(0, 100);
	}, [options, q]);

	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
				setQ(null);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	const handleSelect = (o: string) => {
		onSelect(o);
		setOpen(false);
		setQ(null);
	};

	return (
		<div ref={ref} style={{ position: "relative", display: "inline-block", width }}>
			<input
				ref={inputRef}
				value={displayQ}
				placeholder={placeholder}
				onFocus={() => { setOpen(true); setQ(""); }}
				onChange={(e) => { setQ(e.target.value); setOpen(true); }}
				onKeyDown={(e) => {
					if (e.key === "Escape") { setOpen(false); setQ(null); }
					if (e.key === "Enter" && filtered.length === 1) { handleSelect(filtered[0]!); }
				}}
				style={{
					width: "100%",
					padding: "2px 6px",
					borderRadius: 4,
					border: "1px solid var(--border)",
					background: "var(--bg-primary, #0a0b0d)",
					color: "var(--text-1, #f2f5fa)",
					fontSize: "0.82em",
					outline: open ? "1px solid var(--gold)" : "none",
					boxSizing: "border-box",
				}}
			/>
			{open && filtered.length > 0 ? (
				<div style={{
					position: "fixed",
					zIndex: 99999,
					background: "#1a1d24",
					border: "1px solid var(--border)",
					borderRadius: 6,
					boxShadow: "0 8px 32px rgba(0,0,0,0.85)",
					width: Math.max(width, 230),
					maxHeight: 300,
					overflowY: "auto",
					marginTop: 2,
					// Position below the input
					top: (() => {
						if (!inputRef.current) return "auto";
						const r = inputRef.current.getBoundingClientRect();
						return r.bottom + 2;
					})(),
					left: (() => {
						if (!inputRef.current) return "auto";
						const r = inputRef.current.getBoundingClientRect();
						return r.left;
					})(),
				}}>
					{filtered.map((o) => (
							<div
								key={o}
								onMouseDown={(e) => { e.preventDefault(); handleSelect(o); }}
								style={{
									padding: "5px 10px",
									cursor: "pointer",
									fontSize: "0.82em",
									color: o === value ? "var(--gold)" : "#e8eaf0",
									background: o === value ? "rgba(255,180,0,0.08)" : "transparent",
								}}
								onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.1)"; }}
								onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = o === value ? "rgba(255,180,0,0.08)" : "transparent"; }}
							>
								{o}
							</div>
					))}
				</div>
			) : null}
		</div>
	);
};

// ── Inline editable cells ─────────────────────────────────────────────────────

const EditableText = ({
	value,
	onSave,
	style = {},
}: {
	value: string;
	onSave: (v: string) => void;
	style?: React.CSSProperties;
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value);

	const commit = () => {
		if (draft.trim() !== value) onSave(draft.trim());
		setEditing(false);
	};

	if (editing) {
		return (
			<input
				autoFocus
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
				style={{ width: 90, fontSize: "0.84em", ...style }}
			/>
		);
	}
	return (
		<span
			title="Click to edit"
			onClick={() => { setDraft(value); setEditing(true); }}
			style={{ cursor: "pointer", borderBottom: "1px dashed var(--border)", ...style }}
		>
			{value || <span style={{ color: "var(--muted)" }}>—</span>}
		</span>
	);
};

const RatingCell = ({
	pid,
	rkey,
	value,
	onSave,
}: {
	pid: number;
	rkey: string;
	value: number;
	onSave: (pid: number, key: string, val: number) => void;
}) => {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(String(value));

	const commit = () => {
		const n = parseInt(draft, 10);
		if (!isNaN(n) && n !== value) onSave(pid, rkey, n);
		setEditing(false);
	};

	if (!editing) {
		return (
			<td
				className="num"
				style={{ cursor: "pointer", userSelect: "none" }}
				title={`Click to edit ${rkey}`}
				onClick={() => { setDraft(String(value)); setEditing(true); }}
			>
				<span style={{ color: ratColor(value) }}>{value}</span>
			</td>
		);
	}
	return (
		<td className="num">
			<input
				type="number" min={0} max={100} value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={commit}
				onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
				style={{ width: 44, fontSize: "0.85em" }}
				autoFocus
			/>
		</td>
	);
};

// ── SortableHeader ────────────────────────────────────────────────────────────

const SortableHeader = ({
	label,
	sortKey,
	current,
	dir,
	onClick,
	className,
}: {
	label: string;
	sortKey: SortKey;
	current: SortKey;
	dir: SortDir;
	onClick: (k: SortKey) => void;
	className?: string;
}) => {
	const active = current === sortKey;
	return (
		<th
			className={className}
			onClick={() => onClick(sortKey)}
			style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
		>
			{label}
			{active ? (dir === "asc" ? " ▲" : " ▼") : <span style={{ opacity: 0.3 }}> ⇅</span>}
		</th>
	);
};

// ── PlayerRow ─────────────────────────────────────────────────────────────────

const PlayerRow = ({
	p,
	showRatings,
	showContract,
	showDraft,
	showStats,
	onSave,
	onSaveField,
	onOpenPlayer,
	collegeList,
}: {
	p: EditorPlayer;
	showRatings: boolean;
	showContract: boolean;
	showDraft: boolean;
	showStats: boolean;
	onSave: (pid: number, key: string, val: number) => void;
	onSaveField: (pid: number, field: string, value: string) => void;
	onOpenPlayer: (pid: number) => void;
	collegeList: string[];
}) => {
	const hasRatings = Object.keys(p.ratings).length > 0;

	return (
		<tr>
			{/* Name */}
			<td style={{ whiteSpace: "nowrap" }}>
				<button
					type="button"
					className="player-link"
					style={{ fontSize: "0.88em", marginRight: 4 }}
					onClick={() => onOpenPlayer(p.pid)}
				>
					▶
				</button>
				<EditableText
					value={p.firstName}
					onSave={(v) => onSaveField(p.pid, "firstName", v)}
					style={{ marginRight: 3 }}
				/>
				{" "}
				<EditableText
					value={p.lastName}
					onSave={(v) => onSaveField(p.pid, "lastName", v)}
				/>
			</td>

			{/* Pos */}
			<td style={{ fontSize: "0.82em" }}>
				<SearchableDropdown
					options={POSITIONS}
					value={p.pos}
					onSelect={(v) => onSaveField(p.pid, "pos", v)}
					width={58}
				/>
			</td>

			{/* Age */}
			<td className="num" style={{ fontSize: "0.82em", color: "var(--muted)" }}>{p.age}</td>

			{/* OVR */}
			{p.isCollegePlayer || p.isHsPlayer ? (
				<RatingCell pid={p.pid} rkey="ovr" value={p.ovr} onSave={onSave} />
			) : (
				<td className="num">
					<span style={{ color: ovrColor(p.ovr) }}>{p.ovr}</span>
				</td>
			)}

			{/* POT */}
			<td className="num" style={{ color: "var(--muted)" }}>{p.pot}</td>

			{/* College year badge */}
			{p.year != null ? (
				<td style={{ fontSize: "0.78em" }}>
					<span style={{
						background: p.year==="FR" ? "#3b82f6" : p.year==="SO" ? "#10b981" : p.year==="JR" ? "#f59e0b" : "#ef4444",
						color:"#fff", borderRadius:3, padding:"1px 5px",
					}}>
						{p.year}
					</span>
				</td>
			) : null}

			{/* Contract */}
			{showContract ? (
				p.contract ? (
					<>
						<td className="num" style={{ fontSize: "0.82em" }}>${(p.contract.amount/1000).toFixed(1)}M</td>
						<td className="num" style={{ fontSize: "0.82em" }}>{p.contract.exp}</td>
					</>
				) : <><td /><td /></>
			) : null}

			{/* Draft */}
			{showDraft ? (
				p.draft ? (
					<>
						<td className="num" style={{ fontSize: "0.82em" }}>{p.draft.year}</td>
						<td className="num" style={{ fontSize: "0.82em" }}>
							{p.draft.round > 0 ? `R${p.draft.round} P${p.draft.pick}` : "—"}
						</td>
					</>
				) : <><td /><td /></>
			) : null}

			{/* Hometown */}
			<td style={{ minWidth: 140 }}>
				<SearchableDropdown
					options={US_CITIES}
					value={p.country?.includes(",") || US_CITIES.includes(p.country) ? (p.hometown ?? p.country ?? "") : ""}
					onSelect={(v) => onSaveField(p.pid, "hometown", v)}
					placeholder="Hometown"
					width={150}
				/>
			</td>

			{/* School / College */}
			<td style={{ minWidth: 140 }}>
				<SearchableDropdown
					options={collegeList}
					value={p.college ?? ""}
					onSelect={(v) => onSaveField(p.pid, "college", v)}
					placeholder="College"
					width={150}
				/>
			</td>

			{/* Country */}
			<td style={{ minWidth: 100 }}>
				<SearchableDropdown
					options={COUNTRIES}
					value={p.country && !US_CITIES.includes(p.country) && !p.country.includes(",") ? p.country : "USA"}
					onSelect={(v) => onSaveField(p.pid, "country", v)}
					placeholder="Country"
					width={100}
				/>
			</td>

			{/* College stats */}
			{showStats ? (
				<>
					<td className="num" style={{ fontSize: "0.82em" }}>{p.stats?.pts?.toFixed(1) ?? "—"}</td>
					<td className="num" style={{ fontSize: "0.82em" }}>{p.stats?.reb?.toFixed(1) ?? "—"}</td>
					<td className="num" style={{ fontSize: "0.82em" }}>{p.stats?.ast?.toFixed(1) ?? "—"}</td>
					<td className="num" style={{ fontSize: "0.82em", color: "var(--muted)" }}>{p.stats?.gp ?? "—"}</td>
				</>
			) : null}

			{/* Ratings */}
			{showRatings ? (
				hasRatings ? (
					RATING_KEYS.map((k) => (
						<RatingCell
							key={k}
							pid={p.pid}
							rkey={k}
							value={p.ratings[k] ?? 0}
							onSave={onSave}
						/>
					))
				) : (
					RATING_KEYS.map((k) => (
						<td key={k} className="num" style={{ color: "var(--muted)", fontSize: "0.78em" }}>—</td>
					))
				)
			) : null}
		</tr>
	);
};

// ── PlayerTable ───────────────────────────────────────────────────────────────

const PlayerTable = ({
	players,
	showRatings,
	showContract = false,
	showDraft = false,
	showStats = false,
	hasYear = false,
	onSave,
	onSaveField,
	onOpenPlayer,
	collegeList,
}: {
	players: EditorPlayer[];
	showRatings: boolean;
	showContract?: boolean;
	showDraft?: boolean;
	showStats?: boolean;
	hasYear?: boolean;
	onSave: (pid: number, key: string, val: number) => void;
	onSaveField: (pid: number, field: string, value: string) => void;
	onOpenPlayer: (pid: number) => void;
	collegeList: string[];
}) => {
	const [sortKey, setSortKey] = useState<SortKey>("ovr");
	const [sortDir, setSortDir] = useState<SortDir>("desc");

	const handleSort = useCallback((k: SortKey) => {
		setSortDir((d) => (k === sortKey ? (d === "asc" ? "desc" : "asc") : "desc"));
		setSortKey(k);
	}, [sortKey]);

	const sorted = useMemo(() => {
		const arr = [...players];
		arr.sort((a, b) => {
			let av: any, bv: any;
			switch (sortKey) {
				case "name": av = `${a.lastName} ${a.firstName}`; bv = `${b.lastName} ${b.firstName}`; break;
				case "pos": av = a.pos; bv = b.pos; break;
				case "age": av = a.age; bv = b.age; break;
				case "ovr": av = a.ovr; bv = b.ovr; break;
				case "pot": av = a.pot; bv = b.pot; break;
				case "school": av = a.college || a.teamAbbrev || ""; bv = b.college || b.teamAbbrev || ""; break;
				case "rank": av = a.rank ?? 999; bv = b.rank ?? 999; break;
				case "pts": av = a.stats?.pts ?? 0; bv = b.stats?.pts ?? 0; break;
				case "reb": av = a.stats?.reb ?? 0; bv = b.stats?.reb ?? 0; break;
				case "ast": av = a.stats?.ast ?? 0; bv = b.stats?.ast ?? 0; break;
				default:
					av = a.ratings[sortKey] ?? 0;
					bv = b.ratings[sortKey] ?? 0;
			}
			if (av < bv) return sortDir === "asc" ? -1 : 1;
			if (av > bv) return sortDir === "asc" ? 1 : -1;
			return 0;
		});
		return arr;
	}, [players, sortKey, sortDir]);

	const sh = (label: string, k: SortKey, cls?: string) => (
		<SortableHeader label={label} sortKey={k} current={sortKey} dir={sortDir} onClick={handleSort} className={cls} />
	);

	return (
		<div className="panel-body" style={{ padding: 0, overflowX: "auto" }}>
			<table className="data-table" style={{ minWidth: showRatings ? 1600 : 640 }}>
				<thead>
					<tr>
						{sh("Player","name")}
						{sh("Pos","pos")}
						{sh("Age","age","num")}
						{sh("Ovr","ovr","num")}
						{sh("Pot","pot","num")}
						{hasYear ? <th>Yr</th> : null}
						{showContract ? <><th className="num">Salary</th><th className="num">Exp</th></> : null}
						{showDraft ? <><th className="num">D.Year</th><th className="num">Pick</th></> : null}
						<th>Hometown</th>
						{sh("College","school")}
						<th>Country</th>
						{showStats ? (
							<>
								{sh("PTS","pts","num")}
								{sh("REB","reb","num")}
								{sh("AST","ast","num")}
								<th className="num">GP</th>
							</>
						) : null}
						{showRatings ? RATING_KEYS.map((k) => (
							<th key={k} className="num" title={k} style={{ fontSize:"0.78em", padding:"2px 4px", cursor:"pointer" }}
								onClick={() => handleSort(k)}
							>
								{RATING_LABELS[k]}
								{sortKey === k ? (sortDir==="asc" ? " ▲":" ▼") : <span style={{opacity:0.3}}> ⇅</span>}
							</th>
						)) : null}
					</tr>
				</thead>
				<tbody>
					{sorted.map((p) => (
						<PlayerRow
							key={p.pid}
							p={p}
							showRatings={showRatings}
							showContract={showContract}
							showDraft={showDraft}
							showStats={showStats}
							onSave={onSave}
							onSaveField={onSaveField}
							onOpenPlayer={onOpenPlayer}
							collegeList={collegeList}
						/>
					))}
					{sorted.length === 0 && (
						<tr><td colSpan={30} className="empty">No players.</td></tr>
					)}
				</tbody>
			</table>
		</div>
	);
};

// ── WBBL Push Panel ───────────────────────────────────────────────────────────

const ADMIN_TOKEN_KEY = "wbbl-admin-token";
const API_URL_KEY = "wbbl-api-url";

const WbblPushPanel = ({ setToast }: { setToast: (msg: string | null) => void }) => {
	const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) ?? "");
	const [apiUrl, setApiUrl] = useState(() => localStorage.getItem(API_URL_KEY) ?? "https://wbbl-api.wbbl.workers.dev");
	const [pushing, setPushing] = useState(false);
	const [showConfig, setShowConfig] = useState(false);

	const saveConfig = () => {
		localStorage.setItem(ADMIN_TOKEN_KEY, token);
		localStorage.setItem(API_URL_KEY, apiUrl);
		setShowConfig(false);
		setToast("Config saved.");
		setTimeout(() => setToast(null), 3000);
	};

	const handlePush = async () => {
		if (!token.trim()) { setShowConfig(true); setToast("Enter your admin token first."); setTimeout(() => setToast(null), 4000); return; }
		setPushing(true);
		setToast("Reading league data…");
		try {
			const { makeExportStream } = await import("../../ui/util/exportLeague.ts");
			const stream = await makeExportStream(
				["players","releasedPlayers","awards","teams","teamSeasons","teamStats","gameAttributes","schedule","playoffSeries","draftPicks","trade","negotiations","draftLotteryResults","events","headToHeads","scheduledEvents"],
				{ compressed: true, name: "WBBL" },
			);
			const reader = stream.getReader();
			const parts: string[] = [];
			let done = false;
			while (!done) {
				const { value, done: d } = await reader.read();
				if (d) { done = true; } else { parts.push(value); }
			}
			const json = parts.join("");
			setToast("Uploading to WBBL…");
			const storedUrl = localStorage.getItem(API_URL_KEY) ?? apiUrl;
			const res = await fetch(`${storedUrl}/api/league/snapshot`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY) ?? token}` },
				body: json,
			});
			if (res.ok) {
				const data = await res.json() as { ok: boolean; version?: number };
				setToast(`Live! All browsers now on version ${data.version ?? "?"}. Push complete.`);
				setTimeout(() => setToast(null), 8000);
			} else {
				const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string };
				setToast(`Push failed: ${data.error ?? res.status}`);
				setTimeout(() => setToast(null), 6000);
			}
		} catch (e: any) {
			setToast("Push failed: " + String(e?.message ?? e));
			setTimeout(() => setToast(null), 6000);
		} finally {
			setPushing(false);
		}
	};

	return (
		<div className="panel" style={{ marginBottom: 16, padding: "14px 16px" }}>
			<div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
				<div style={{ flex: 1, minWidth: 220 }}>
					<div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--white)" }}>Push League to WBBL</div>
					<div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 3 }}>
						Exports the current league and uploads it live. Every browser visitor sees it instantly — no redeploy needed.
					</div>
					<div style={{ fontSize: 11, color: "var(--text-4)", marginTop: 6 }}>
						API: <code style={{ color: "var(--gold)", fontSize: 11 }}>{apiUrl}</code>
					</div>
				</div>
				<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
					<button type="button" className="btn btn-light-bordered" style={{ fontSize: 12 }} onClick={() => setShowConfig((s) => !s)}>
						{showConfig ? "Hide Config" : "Config"}
					</button>
					<button type="button" className="btn btn-primary" disabled={pushing} onClick={handlePush}>
						{pushing ? "Pushing…" : "Push to WBBL"}
					</button>
				</div>
			</div>
			{showConfig ? (
				<div style={{ marginTop: 12, display: "grid", gap: 8, gridTemplateColumns: "auto 1fr", alignItems: "center", maxWidth: 540 }}>
					<label style={{ fontSize: 12, color: "var(--text-3)" }}>API URL</label>
					<input className="form-control form-control-sm" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} placeholder="https://wbbl-api.<account>.workers.dev" />
					<label style={{ fontSize: 12, color: "var(--text-3)" }}>Admin Token</label>
					<input className="form-control form-control-sm" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Secret set during worker deployment" />
					<div />
					<button type="button" className="btn btn-sm btn-secondary" onClick={saveConfig}>Save Config</button>
				</div>
			) : null}
		</div>
	);
};

// ── Tab type ──────────────────────────────────────────────────────────────────

type TabId = "nba" | "fa" | "draft" | "college" | "hs" | "hsjr" | "retired" | "international";

// ── RosterEditor ──────────────────────────────────────────────────────────────

export const RosterEditor = () => {
	const openPlayer = useDesktopStore((s) => s.openPlayer);
	const [data, setData] = useState<EditorData | null>(null);
	const [loading, setLoading] = useState(true);
	const [showRatings, setShowRatings] = useState(true);
	const [search, setSearch] = useState("");
	const [tab, setTab] = useState<TabId>("nba");
	const [selectedTeam, setSelectedTeam] = useState<number | "all">("all");
	const [selectedCollege, setSelectedCollege] = useState<number | "all">("all");
	const [toast, setToast] = useState<string | null>(null);
	const [draftYearFilter, setDraftYearFilter] = useState<number | "all">("all");
	const [collegeList, setCollegeList] = useState<string[]>([]);

	const load = async () => {
		setLoading(true);
		const [result, colleges] = await Promise.all([
			toWorker("main", "getRosterEditorData", undefined).catch(() => null),
			toWorker("main", "getCollegeList", undefined).catch(() => [] as string[]),
		]);
		setData(result as EditorData | null);
		setCollegeList((colleges as string[]) ?? []);
		setLoading(false);
	};

	useEffect(() => { void load(); }, []);

	const onSave = async (pid: number, key: string, value: number) => {
		// For college/HS players use the in-memory update
		const isCollegeOrHs = pid < 0 || (data && (
			data.collegeTeams.some((ct) => ct.players.some((p) => p.pid === pid)) ||
			data.hsTop100.some((p: any) => p.pid === pid) ||
			data.hsJuniors.some((p: any) => p.pid === pid)
		));

		let newOvr: number | undefined;
		let newPot: number | undefined;

		if (isCollegeOrHs && key === "ovr") {
			const res = await toWorker("main", "setCollegePlayerOvr", { pid, value }).catch(() => ({ ok: false, ovr: value })) as { ok: boolean; ovr: number };
			newOvr = res.ovr;
		} else {
			const res = await toWorker("main", "setPlayerRating", { pid, key, value }).catch(() => ({ ok: false, ovr: 0, pot: 0 })) as { ok: boolean; ovr: number; pot: number };
			newOvr = res.ovr;
			newPot = res.pot;
		}

		// Optimistic local update
		setData((prev) => {
			if (!prev) return prev;
			const updateP = (p: EditorPlayer) => {
				if (p.pid !== pid) return p;
				const updated = { ...p, ratings: { ...p.ratings, [key]: value } };
				if (newOvr !== undefined) updated.ovr = newOvr;
				if (newPot !== undefined) updated.pot = newPot;
				return updated;
			};
			return {
				...prev,
				teamRosters: prev.teamRosters.map((t) => ({ ...t, players: t.players.map(updateP) })),
				freeAgents: prev.freeAgents.map(updateP),
				draftClasses: prev.draftClasses.map(updateP),
				retired: prev.retired.map(updateP),
				collegeTeams: prev.collegeTeams.map((ct) => ({ ...ct, players: ct.players.map(updateP) })),
				hsTop100: prev.hsTop100.map(updateP as any),
				hsJuniors: prev.hsJuniors.map(updateP as any),
			};
		});

		setToast(newOvr !== undefined ? `Saved — OVR now ${newOvr}` : `Saved ${key}=${value}`);
		setTimeout(() => setToast(null), 2500);
	};

	const onSaveField = async (pid: number, field: string, value: string) => {
		await toWorker("main", "setPlayerField", { pid, field, value }).catch(() => null);

		// Optimistic local update
		setData((prev) => {
			if (!prev) return prev;
			const updateP = (p: EditorPlayer) => {
				if (p.pid !== pid) return p;
				if (field === "firstName") return { ...p, firstName: value };
				if (field === "lastName") return { ...p, lastName: value };
				if (field === "college") return { ...p, college: value };
				if (field === "hometown") return { ...p, hometown: value, country: value };
				if (field === "pos") return { ...p, pos: value };
				if (field === "country") return { ...p, country: value };
				return p;
			};
			return {
				...prev,
				teamRosters: prev.teamRosters.map((t) => ({ ...t, players: t.players.map(updateP) })),
				freeAgents: prev.freeAgents.map(updateP),
				draftClasses: prev.draftClasses.map(updateP),
				retired: prev.retired.map(updateP),
				collegeTeams: prev.collegeTeams.map((ct) => ({ ...ct, players: ct.players.map(updateP) })),
				hsTop100: prev.hsTop100.map(updateP as any),
				hsJuniors: prev.hsJuniors.map(updateP as any),
			};
		});

		setToast(`Updated ${field}`);
		setTimeout(() => setToast(null), 2000);
	};

	const filter = (players: EditorPlayer[]) => {
		if (!search.trim()) return players;
		const q = search.toLowerCase();
		return players.filter((p) =>
			`${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
			p.pos.toLowerCase().includes(q) ||
			(p.college ?? "").toLowerCase().includes(q) ||
			(p.country ?? "").toLowerCase().includes(q) ||
			(p.hometown ?? "").toLowerCase().includes(q),
		);
	};

	const draftYears = useMemo(() => {
		if (!data) return [];
		return [...new Set(data.draftClasses.map((p) => p.draft?.year ?? 0))].sort();
	}, [data]);

	const filteredDraft = useMemo(() => {
		if (!data) return [];
		const base = draftYearFilter === "all" ? data.draftClasses : data.draftClasses.filter((p) => p.draft?.year === draftYearFilter);
		return filter(base);
	}, [data, draftYearFilter, search]);

	const intlPlayers = useMemo(() => {
		if (!data) return [];
		const all = [...data.teamRosters.flatMap((t) => t.players), ...data.freeAgents];
		return filter(all.filter((p) => p.country && !p.country.toLowerCase().includes("usa") && !p.country.toLowerCase().includes("united states")));
	}, [data, search]);

	if (loading) {
		return (
			<>
				<h1 className="page-title">Roster Editor</h1>
				<p className="page-sub muted">Loading all player data…</p>
			</>
		);
	}
	if (!data) {
		return (
			<>
				<h1 className="page-title">Roster Editor</h1>
				<p className="page-sub" style={{ color: "var(--red)" }}>Failed to load. Is a league open?</p>
			</>
		);
	}

	const TABS: { id: TabId; label: string; count?: number }[] = [
		{ id: "nba", label: "NBA Rosters", count: data.teamRosters.reduce((n, t) => n + t.players.length, 0) },
		{ id: "fa", label: "Free Agents", count: data.freeAgents.length },
		{ id: "draft", label: "Draft Classes", count: data.draftClasses.length },
		{ id: "college", label: "College D1", count: data.collegeTeams.reduce((n, t) => n + t.players.length, 0) },
		{ id: "hs", label: "HS Seniors", count: data.hsTop100.length },
		{ id: "hsjr", label: "HS Juniors", count: data.hsJuniors.length },
		{ id: "international", label: "International", count: intlPlayers.length },
		{ id: "retired", label: "Retired", count: data.retired.length },
	];

	const tableProps = { showRatings, onSave, onSaveField, onOpenPlayer: openPlayer, collegeList };

	return (
		<>
			{toast ? (
				<div style={{ position:"fixed", top:14, right:14, background:"var(--green)", color:"#fff", padding:"6px 14px", borderRadius:6, zIndex:9999, fontSize:"0.9em" }}>
					{toast}
				</div>
			) : null}

			<h1 className="page-title">Roster Editor</h1>
			<p className="page-sub">
				All players with <strong>real ratings</strong>. Click any cell to edit.
				Click <strong>▶</strong> to open player card. Season: {data.season}.
			</p>

			<WbblPushPanel setToast={setToast} />

			{/* Controls */}
			<div style={{ display:"flex", gap:10, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
				<input
					type="search"
					placeholder="Search player, pos, school…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					style={{ padding:"5px 10px", borderRadius:6, border:"1px solid var(--border)", background:"var(--bg-secondary)", color:"inherit", width:220 }}
				/>
				<label style={{ display:"flex", alignItems:"center", gap:6, fontSize:"0.88em", cursor:"pointer" }}>
					<input type="checkbox" checked={showRatings} onChange={(e) => setShowRatings(e.target.checked)} />
					Show all ratings
				</label>
				<button type="button" className="btn btn-secondary" onClick={load} style={{ fontSize:"0.85em" }}>↻ Refresh</button>
			</div>

			{/* Tab bar */}
			<div style={{ display:"flex", gap:6, marginBottom:14, flexWrap:"wrap" }}>
				{TABS.map(({ id, label, count }) => (
					<button
						key={id} type="button"
						className={`btn ${tab === id ? "btn-primary" : "btn-secondary"}`}
						style={{ fontSize:"0.82em", padding:"4px 10px" }}
						onClick={() => setTab(id)}
					>
						{label}
						{count != null ? <span style={{ marginLeft:5, opacity:0.7, fontSize:"0.88em" }}>({count})</span> : null}
					</button>
				))}
			</div>

			{/* NBA Rosters */}
			{tab === "nba" ? (
				<>
					<div style={{ marginBottom:10, display:"flex", gap:8, alignItems:"center" }}>
						<label style={{ fontSize:"0.88em" }}>Team:</label>
						<select
							value={selectedTeam === "all" ? "all" : String(selectedTeam)}
							onChange={(e) => setSelectedTeam(e.target.value === "all" ? "all" : Number(e.target.value))}
							style={{ padding:"3px 8px", borderRadius:5, background:"#1a1d24", color:"#f2f5fa", border:"1px solid var(--border)" }}
						>
							<option value="all">All Teams</option>
							{data.teamRosters.map((t) => (
								<option key={t.tid} value={t.tid}>{t.team.region} {t.team.name} ({t.players.length})</option>
							))}
						</select>
					</div>
					{data.teamRosters
						.filter((t) => selectedTeam === "all" || t.tid === selectedTeam)
						.map((t) => {
							const players = filter(t.players);
							if (players.length === 0 && search.trim()) return null;
							return (
								<section className="panel" key={t.tid} style={{ marginBottom:14 }}>
									<div className="panel-header">
										<span>{t.team.region} {t.team.name}</span>
										<span className="pill">{players.length} players</span>
									</div>
									<PlayerTable players={players} showContract {...tableProps} />
								</section>
							);
						})}
				</>
			) : null}

			{/* Free Agents */}
			{tab === "fa" ? (
				<section className="panel">
					<div className="panel-header"><span>Free Agents</span><span className="pill">{filter(data.freeAgents).length}</span></div>
					<PlayerTable players={filter(data.freeAgents)} showContract {...tableProps} />
				</section>
			) : null}

			{/* Draft Classes */}
			{tab === "draft" ? (
				<>
					<div style={{ marginBottom:10, display:"flex", gap:8, alignItems:"center" }}>
						<label style={{ fontSize:"0.88em" }}>Draft Year:</label>
						<select
							value={draftYearFilter === "all" ? "all" : String(draftYearFilter)}
							onChange={(e) => setDraftYearFilter(e.target.value === "all" ? "all" : Number(e.target.value))}
							style={{ padding:"3px 8px", borderRadius:5, background:"#1a1d24", color:"#f2f5fa", border:"1px solid var(--border)" }}
						>
							<option value="all">All Years</option>
							{draftYears.map((y) => <option key={y} value={y}>{y}</option>)}
						</select>
					</div>
					<section className="panel">
						<div className="panel-header"><span>Draft Classes</span><span className="pill">{filteredDraft.length} prospects</span></div>
						<PlayerTable players={filteredDraft} showDraft {...tableProps} />
					</section>
				</>
			) : null}

			{/* College D1 */}
			{tab === "college" ? (
				<>
					<div style={{ marginBottom:10, display:"flex", gap:8, alignItems:"center" }}>
						<label style={{ fontSize:"0.88em" }}>School:</label>
						<select
							value={selectedCollege === "all" ? "all" : String(selectedCollege)}
							onChange={(e) => setSelectedCollege(e.target.value === "all" ? "all" : Number(e.target.value))}
							style={{ padding:"3px 8px", borderRadius:5, background:"#1a1d24", color:"#f2f5fa", border:"1px solid var(--border)", maxWidth:300 }}
						>
							<option value="all">All Schools</option>
							{data.collegeTeams.map((ct) => (
								<option key={ct.tid} value={ct.tid}>{ct.school} ({ct.players.length})</option>
							))}
						</select>
					</div>
					{data.collegeTeams
						.filter((ct) => selectedCollege === "all" || ct.tid === selectedCollege)
						.map((ct) => {
							const players = filter(ct.players);
							if (players.length === 0 && search.trim()) return null;
							return (
								<section className="panel" key={ct.tid} style={{ marginBottom:14 }}>
									<div className="panel-header">
										<span>{ct.school}</span>
										<span className="pill">{players.length} players</span>
									</div>
									<PlayerTable players={players} hasYear showStats {...tableProps} />
								</section>
							);
						})}
				</>
			) : null}

			{/* HS Seniors */}
			{tab === "hs" ? (
				<section className="panel">
					<div className="panel-header">
						<span>HS Senior Class — Projected {data.season + 1} Draft</span>
						<span className="pill">{filter(data.hsTop100 as any[]).length} prospects</span>
					</div>
					<PlayerTable
						players={(data.hsTop100 as any[]).map((p: any) => ({
							...p,
							isHsPlayer: true,
							ratings: p.ratings ?? {},
						}))}
						{...tableProps}
					/>
				</section>
			) : null}

			{/* HS Juniors */}
			{tab === "hsjr" ? (
				<section className="panel">
					<div className="panel-header">
						<span>HS Junior Class — Projected {data.season + 2} Draft</span>
						<span className="pill">{filter(data.hsJuniors as any[]).length} prospects</span>
					</div>
					<PlayerTable
						players={(data.hsJuniors as any[]).map((p: any) => ({
							...p,
							isHsPlayer: true,
							ratings: p.ratings ?? {},
						}))}
						{...tableProps}
					/>
				</section>
			) : null}

			{/* International */}
			{tab === "international" ? (
				<section className="panel">
					<div className="panel-header"><span>International Players</span><span className="pill">{intlPlayers.length}</span></div>
					<PlayerTable players={intlPlayers} showContract {...tableProps} />
				</section>
			) : null}

			{/* Retired */}
			{tab === "retired" ? (
				<section className="panel">
					<div className="panel-header"><span>Retired Players</span><span className="pill">{filter(data.retired).length}</span></div>
					<PlayerTable players={filter(data.retired)} {...tableProps} />
				</section>
			) : null}
		</>
	);
};
